import { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { getConfig } from './api';
import { TopBar } from './components/TopBar';
import { AdminSyncHistory } from './pages/AdminSyncHistory';
import { ConfigPage } from './pages/ConfigPage';
import { FormScreen } from './pages/FormScreen';
import { LoginPage } from './pages/LoginPage';
import { StreamScreen } from './pages/StreamScreen';
import { StreamsIndex } from './pages/StreamsIndex';
import { useCurrentUser } from './state/auth';
import { ConfigContext } from './state/config';
import { SessionContext } from './state/session';
import { SyncProvider } from './state/sync';
import type { AppConfig } from './types';

export default function App() {
  const auth = useCurrentUser();

  if (auth.state.status === 'loading') {
    return <FullScreenMessage>Loading…</FullScreenMessage>;
  }

  if (auth.state.status === 'error') {
    return (
      <FullScreenMessage>
        <h1 className="text-lg font-semibold text-slate-900">
          Couldn&apos;t reach the backend
        </h1>
        <p className="mt-2 text-sm text-slate-600 break-words">
          {auth.state.error}
        </p>
      </FullScreenMessage>
    );
  }

  if (auth.state.status === 'unauthenticated') {
    return <LoginPage />;
  }

  if (auth.state.status === 'noAccess') {
    return (
      <LoginPage
        noAccessHint={{ email: auth.state.email, detail: auth.state.detail }}
      />
    );
  }

  return (
    <SessionContext.Provider value={auth.state.user}>
      <AuthenticatedShell signOut={auth.signOut} />
    </SessionContext.Provider>
  );
}

function AuthenticatedShell({ signOut }: { signOut: () => Promise<void> }) {
  const [cfg, setCfg] = useState<AppConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getConfig()
      .then((c) => {
        if (!cancelled) setCfg(c);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <FullScreenMessage>
        <h1 className="text-lg font-semibold text-slate-900">
          Couldn&apos;t load config
        </h1>
        <p className="mt-2 text-sm text-slate-600 break-words">{error}</p>
      </FullScreenMessage>
    );
  }

  if (!cfg) {
    return <FullScreenMessage>Loading…</FullScreenMessage>;
  }

  return (
    <ConfigContext.Provider value={cfg}>
      <SyncProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-slate-50">
            <TopBar onSignOut={signOut} />
            <main>
              <Routes>
                <Route path="/" element={<StreamsIndex />} />
                <Route path="/admin/sync" element={<AdminSyncHistory />} />
                <Route path="/config" element={<ConfigPage />} />
                <Route path="/streams/:streamId" element={<StreamScreen />} />
                <Route
                  path="/streams/:streamId/form/sys/:sysId"
                  element={<FormScreen />}
                />
                <Route
                  path="/streams/:streamId/form/:kbNumber"
                  element={<FormScreen />}
                />
                <Route
                  path="*"
                  element={
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-slate-500">
                      Not found.
                    </div>
                  }
                />
              </Routes>
            </main>
          </div>
        </BrowserRouter>
      </SyncProvider>
    </ConfigContext.Provider>
  );
}

function FullScreenMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 text-slate-500">
      <div className="max-w-md text-center">{children}</div>
    </div>
  );
}
